/**
 * City landing-page content for /chevy-sales-{slug}-oh.
 *
 * STRICT RULES (per user spec):
 *   - No mention of Byers Chevrolet
 *   - No fake LocalBusiness / fake addresses
 *   - No "Scott has an office in [city]" framing
 *   - No demographic / income stereotypes
 *   - Each city: unique H1, unique hero subhead, unique paragraphs,
 *     unique buyer-intent cards, unique vehicle emphasis, unique FAQs
 *   - 4 JSON-LD blocks per page (Breadcrumb, WebPage, Service, FAQPage)
 *
 * Buckets:
 *   suburb-family   → family SUVs, EVs, commuter, lease/pmt help
 *   city-budget     → affordable new/used, payment-conscious, first-time/upgrade buyers
 *   truck-distance  → trucks, used trucks, AWD SUVs, longer-drive shoppers
 */

export const ALL_CITY_SLUGS = [
  "grove-city-oh","columbus-oh","hilliard-oh","dublin-oh","gahanna-oh",
  "westerville-oh","reynoldsburg-oh","pickerington-oh","groveport-oh",
  "canal-winchester-oh","ashville-oh","circleville-oh","london-oh",
  "plain-city-oh","delaware-oh","marysville-oh","pataskala-oh",
  "lancaster-oh","chillicothe-oh","newark-oh","whitehall-oh",
];

// =================== MODEL LIBRARY (city-agnostic) ===================
// Each model entry: { to, bestFor, copy(city) → string }
// Used to render Popular Vehicle cards on every city page.
export const MODEL_LIBRARY = {
  Silverado: {
    to: "/new-vehicles/silverado",
    bestFor: "Hauling, work, weekends",
    copy: (c) => `Real truck capability for ${c} drivers who tow, haul, or just want a vehicle built for actual work. New and used options.`,
  },
  Colorado: {
    to: "/new-vehicles?model=Colorado",
    bestFor: "Mid-size truck",
    copy: (c) => `A mid-size truck that fits ${c} driveways better than a full-size, without giving up real work capability.`,
  },
  Equinox: {
    to: "/new-vehicles/equinox",
    bestFor: "Daily SUV comfort",
    copy: (c) => `Compact Chevy SUV that fits most ${c} budgets and most daily-driver lives. Easy on gas, easy to live with.`,
  },
  Trax: {
    to: "/new-vehicles/trax",
    bestFor: "Lowest-entry SUV",
    copy: (c) => `The most affordable way into a new Chevy SUV for ${c} shoppers — great for first-timers and payment-conscious buyers.`,
  },
  Traverse: {
    to: "/new-vehicles/traverse",
    bestFor: "3-row family hauler",
    copy: (c) => `Three rows of seating, real cargo room, and the size most ${c} families actually need.`,
  },
  Tahoe: {
    to: "/new-vehicles/tahoe",
    bestFor: "Full-size SUV",
    copy: (c) => `Big Chevy SUV — space, towing, and presence for ${c} drivers who need it all in one vehicle.`,
  },
  Suburban: {
    to: "/new-vehicles/suburban",
    bestFor: "Maximum space + tow",
    copy: (c) => `The longest Chevy SUV. For ${c} families and shoppers who need every inch of cargo and third-row space.`,
  },
  Malibu: {
    to: "/new-vehicles?model=Malibu",
    bestFor: "Mid-size sedan",
    copy: (c) => `Mid-size sedan with sedan fuel costs — a smart pick for ${c} shoppers who want lower payment territory.`,
  },
  "Equinox EV": {
    to: "/new-vehicles?model=Equinox+EV",
    bestFor: "Lowest-entry Chevy EV",
    copy: (c) => `The most affordable way into a Chevy EV for ${c} drivers. Good range and a real SUV body style.`,
  },
  "Blazer EV": {
    to: "/new-vehicles/blazer",
    bestFor: "Mid-size EV SUV",
    copy: (c) => `Midsize Chevy EV with strong range options — a real fit for ${c} drivers ready to compare EV vs gas honestly.`,
  },
  "Silverado EV": {
    to: "/new-vehicles?model=Silverado+EV",
    bestFor: "EV truck capability",
    copy: (c) => `Truck without the gas. For ${c} drivers who want real truck capability but care about charging cost vs fuel cost.`,
  },
  "Used trucks": {
    to: "/used-vehicles/trucks",
    bestFor: "Trade-friendly used",
    copy: (c) => `Used and fresh-trade trucks across price ranges. The bread and butter of practical ${c} shoppers.`,
  },
  "Used SUVs": {
    to: "/used-vehicles/suvs",
    bestFor: "Used SUV variety",
    copy: (c) => `Used SUVs across body styles, mileage, and price for ${c} buyers comparing total cost honestly.`,
  },
  "Used cars": {
    to: "/used-vehicles/cars",
    bestFor: "Lower payment territory",
    copy: (c) => `Used cars when ${c} payment goals matter more than the newest tech.`,
  },
  "Used under $25k": {
    to: "/used-vehicles/under-25000",
    bestFor: "Payment-friendly used",
    copy: (c) => `Used inventory under the $25k mark — useful when ${c} payment range is the top priority.`,
  },
  "AWD SUVs": {
    to: "/shop-by-need/awd-4x4",
    bestFor: "Ohio winter ready",
    copy: (c) => `All-wheel-drive SUVs for ${c} drivers who deal with real Ohio winters and country roads.`,
  },
};

// =================== BUYER-INTENT CARD ARCHETYPES ===================
// 4 cards per city. Cities mix-and-match from these archetypes via `buyerIntent`.
const ARCHETYPES = {
  familySUVs: {
    icon: "users",
    title: "Family SUVs",
    body: (c) => `Equinox, Traverse, Tahoe, and Suburban for ${c} families that need space, comfort, and modern tech without overpaying for trim levels you'll never use.`,
    to: "/new-vehicles?body=suv",
  },
  chevyEVs: {
    icon: "zap",
    title: "Chevy EVs",
    body: (c) => `Equinox EV, Blazer EV, and Silverado EV for ${c} shoppers honestly comparing range, charging, payment, and long-term fit — not just hype.`,
    to: "/ev-help",
  },
  tradeUpgrades: {
    icon: "tag",
    title: "Trade-In Upgrades",
    body: (c) => `Help understanding payoff, equity, mileage, and condition before you trade — so the next vehicle starts on the right footing.`,
    to: "/value-my-trade",
  },
  leaseVsFinance: {
    icon: "scale",
    title: "Lease vs Finance",
    body: (c) => `Compare monthly payment, term, mileage caps, and long-term ownership cost. For ${c} drivers who want clarity, not pressure.`,
    to: "/payment-help",
  },
  chevyTrucks: {
    icon: "truck",
    title: "Chevy Trucks",
    body: (c) => `Silverado and Colorado help for towing, work, hauling, and weekend use — popular with ${c} shoppers who actually use a truck.`,
    to: "/new-vehicles?body=truck",
  },
  usedTrucksSUVs: {
    icon: "tag",
    title: "Used Trucks & SUVs",
    body: (c) => `Help sorting mileage, condition, price, and payment range so ${c} shoppers don't overpay for the wrong used vehicle.`,
    to: "/used-vehicles",
  },
  tradeHelp: {
    icon: "tag",
    title: "Trade-In Help",
    body: (c) => `Understand your trade, payoff, equity, and next steps before driving in. Scott reviews the basics by text.`,
    to: "/value-my-trade",
  },
  longerDriveShopping: {
    icon: "map",
    title: "Longer-Drive Shopping",
    body: (c) => `Narrow real options before making the trip from ${c}. Scott helps you handle 80% of it by text first.`,
    to: "/find-me-a-vehicle",
  },
  affordableHelp: {
    icon: "dollar",
    title: "Affordable Vehicle Help",
    body: (c) => `New and used Chevy options that match real ${c} payment ranges — without the bait-and-switch number games.`,
    to: "/payment-help",
  },
  commuterSUVs: {
    icon: "compass",
    title: "Commuter Vehicles",
    body: (c) => `${c} drivers who run long commutes need fuel sense, tech, and a payment that holds up. Scott helps you compare.`,
    to: "/new-vehicles?body=suv",
  },
  paymentFocused: {
    icon: "dollar",
    title: "Payment-Focused Buying",
    body: (c) => `Start with the payment you actually want to land on and work backwards. ${c} shoppers see way fewer surprise numbers this way.`,
    to: "/payment-help",
  },
  firstTimeBuyer: {
    icon: "help",
    title: "First-Time Buyer Help",
    body: (c) => `Buying a first vehicle in ${c}? Scott walks you through credit basics, down payment, and what to expect from approval — no pressure.`,
    to: "/contact",
  },
  newerVehicles: {
    icon: "car",
    title: "Newer Chevy Vehicles",
    body: (c) => `Compare current Chevy models against your wish list — new tech, current safety, and the right trim for how ${c} drivers actually drive.`,
    to: "/new-vehicles",
  },
  evCurious: {
    icon: "zap",
    title: "EV-Curious?",
    body: (c) => `Scott helps ${c} drivers honestly weigh range, charging, daily miles, and ownership cost before committing to an EV.`,
    to: "/ev-help",
  },
  workTrucks: {
    icon: "truck",
    title: "Work Trucks",
    body: (c) => `Real Silverado and Colorado capability for ${c} shoppers who tow, haul, or use a truck on the clock.`,
    to: "/new-vehicles?body=truck",
  },
  awdNeeds: {
    icon: "shield",
    title: "AWD & 4x4 for Ohio Winters",
    body: (c) => `${c} winters and rural roads make AWD or 4x4 a real conversation — Scott helps you compare SUV and truck options.`,
    to: "/shop-by-need/awd-4x4",
  },
};

// FAQ generator — base 7 questions, plus 1-2 city-bucket flavored questions.
const FAQ_BUILD = (city, opts = {}) => {
  const f = [];
  f.push({
    q: `Do you help ${city} drivers find new Chevy vehicles?`,
    a: `Yes. I help ${city} drivers compare new Chevy cars, trucks, SUVs, and EVs based on budget, trade value, payment goals, and daily driving needs.`,
  });
  f.push({
    q: `Can I text Scott before visiting?`,
    a: `Yes — texting is the easiest way to start. Send what you're looking for, your trade details, your payment range, or questions about a specific vehicle.`,
  });
  f.push({
    q: `Do you help with used vehicles near ${city}?`,
    a: `Yes. ${opts.usedAnchor || `I can help compare used cars, used SUVs, used trucks, mileage, condition, price, and payment fit.`}`,
  });
  f.push({
    q: `Can Scott help value my trade from ${city}?`,
    a: `Yes. Send the year, make, model, mileage, payoff if there is one, condition, and photos if possible. I'll come back with what affects the number and what to expect.`,
  });
  f.push({
    q: `Can Scott help me stay near a certain monthly payment?`,
    a: `Yes. I help you look at realistic options based on payment goal, down payment, trade value, term, lease vs finance, credit situation, and vehicle choice.`,
  });
  if (opts.evHeavy) {
    f.push({
      q: `Do you help explain Chevy EVs around ${city}?`,
      a: `Yes. I compare Equinox EV, Blazer EV, and Silverado EV — range, charging at home, public charging, lease vs finance, and whether an EV actually fits how you drive.`,
    });
  } else {
    f.push({
      q: `Do you help with Chevy EVs if I'm just curious?`,
      a: `Yes, no pressure. EVs aren't right for every driver — I'll walk you through range, charging, and real-world use so you can decide honestly.`,
    });
  }
  f.push({
    q: `Do I need to know exactly what vehicle I want before texting?`,
    a: `No. A rough direction — SUV, truck, EV, used vehicle, family vehicle, or payment range — is plenty. I'll help narrow it from there.`,
  });
  f.push({
    q: `Can Scott help me find a specific vehicle near ${city}?`,
    a: `Yes. Use the Find Me a Vehicle option or text Scott the model, color, payment range, features, and must-haves — I'll watch for the right match.`,
  });
  if (opts.bucketFaq) f.push(opts.bucketFaq);
  return f.slice(0, opts.count || 8);
};

// =================== 21 CITY ENTRIES ===================
export const CITY_DATA = {
  "grove-city-oh": {
    city: "Grove City",
    bucket: "suburb-family",
    image: "downtown",
    h1: "Chevy Truck, SUV, and Payment Help Near Grove City, OH",
    heroSubhead: "Shopping from Grove City? Text Scott for personal help comparing new Chevy trucks, SUVs, EVs, trade value, and payment options before you spend hours digging through listings.",
    metaDesc: "Need help buying a Chevy near Grove City, OH? Text Scott for new Chevy trucks, SUVs, EV options, trade-in, and payment help — no pressure, no runaround.",
    paragraphs: [
      "Grove City sits on the south side of the Columbus metro and most local drivers lean toward practical SUVs, capable trucks, and well-priced used vehicles. Scott helps Grove City shoppers compare new Chevy options against used trucks and SUVs, value trades honestly, and land on a payment range that actually fits the household budget.",
      "Whether you're commuting up I-71 into Columbus, hauling on the weekends, or just need a daily driver that doesn't fall apart at 80,000 miles, Grove City drivers care about resale, reliability, and not getting talked into more vehicle than they need. I'll help you weigh new vs used, look at the right Silverado or Equinox trim, and skip the back-and-forth dealership games.",
      "If you'd rather start by text, send a few details — what you're driving now, what payment range you're trying to stay near, and whether you have a trade. I'll come back with a real direction, not a sales pitch.",
    ],
    buyerIntent: ["chevyTrucks","familySUVs","tradeHelp","paymentFocused"],
    models: ["Silverado","Colorado","Equinox","Trax","Traverse","Tahoe","Used trucks","Used SUVs"],
    evEmphasis: "med",
    nearby: ["columbus-oh","hilliard-oh","groveport-oh","canal-winchester-oh","ashville-oh","london-oh","whitehall-oh"],
    faqs: FAQ_BUILD("Grove City", { evHeavy: false, count: 8, bucketFaq: { q: "Do you help with Chevy trucks from Grove City?", a: "Yes — Silverado, Colorado, and used trucks are some of the most common asks from Grove City shoppers. I'll help you compare bed length, cab style, tow capability, and what's worth new vs used." } }),
  },
  "columbus-oh": {
    city: "Columbus",
    bucket: "city-budget",
    image: "skyline",
    h1: "Affordable New and Used Chevy Help Near Columbus, OH",
    heroSubhead: "Shopping from Columbus? Text Scott for personal help on new and used Chevy options, payment direction, trade value, and EV questions — no pressure, no fake numbers.",
    metaDesc: "Shopping a Chevy in Columbus, OH? Text Scott for new, used, EV, trade-in, and payment help — first-time buyers, upgrade buyers, and budget-conscious shoppers welcome.",
    paragraphs: [
      "Columbus is the biggest market in Central Ohio and the most mixed. First-time buyers, families upgrading from a sedan to an SUV, commuters chasing better fuel economy, and EV-curious drivers all live in the same city — and they all want a fast, honest conversation before they spend hours looking at listings.",
      "From renters near campus and downtown to homeowners in Clintonville, Bexley, or German Village, Columbus shoppers usually start with a payment number in mind, not a specific vehicle. Scott helps you work the math backwards: what payment you want, what your trade is worth, what's available, and what actually fits — instead of just what's on the lot.",
      "Text Scott a few details about how you drive and what you want to spend each month, and you'll get a realistic direction back. No pressure, no surprise numbers later.",
    ],
    buyerIntent: ["affordableHelp","firstTimeBuyer","tradeUpgrades","evCurious"],
    models: ["Trax","Equinox","Malibu","Used cars","Used SUVs","Used under $25k","Equinox EV","Blazer EV"],
    evEmphasis: "high",
    nearby: ["grove-city-oh","whitehall-oh","reynoldsburg-oh","hilliard-oh","gahanna-oh","westerville-oh","dublin-oh","pickerington-oh"],
    faqs: FAQ_BUILD("Columbus", { evHeavy: true, count: 8, bucketFaq: { q: "Can Scott help first-time car buyers in Columbus?", a: "Yes. I walk you through credit basics, down payment, trade equity, and what to expect from the lender step — no pressure, just the honest version." } }),
  },
  "hilliard-oh": {
    city: "Hilliard",
    bucket: "suburb-family",
    image: "suburb",
    h1: "Chevy SUV and Payment Help Near Hilliard, OH",
    heroSubhead: "Shopping from Hilliard? Text Scott for family SUV help, commuter vehicle comparisons, EV questions, and clear payment direction — no pressure.",
    metaDesc: "Buying a Chevy near Hilliard, OH? Text Scott for family SUVs, EVs, commuter help, and lease-vs-finance payment guidance — no pressure, no runaround.",
    paragraphs: [
      "Hilliard runs on family SUVs, commuter crossovers, and a healthy amount of EV curiosity — there's even public charging coverage near Hilliard Station Park. Scott helps Hilliard shoppers compare what fits the daily commute, the weekends, and the payment range you actually want to land on.",
      "Whether you live near Old Hilliard, work along the I-270 corridor, or run kids to Bradley or Davidson, the right vehicle here usually has a third-row option or solid cargo room, decent fuel economy or EV range, and a payment that doesn't strangle the rest of the household. I'll help you weigh new vs used, lease vs finance, and gas vs EV honestly — without pushing you toward the most expensive trim on the lot.",
      "Send a quick text with what you're driving now and what payment you're trying to stay near. I'll come back with a real direction.",
    ],
    buyerIntent: ["familySUVs","chevyEVs","commuterSUVs","leaseVsFinance"],
    models: ["Equinox","Traverse","Trax","Blazer EV","Equinox EV","Silverado","Used SUVs"],
    evEmphasis: "high",
    nearby: ["dublin-oh","columbus-oh","grove-city-oh","gahanna-oh","westerville-oh","plain-city-oh","marysville-oh"],
    faqs: FAQ_BUILD("Hilliard", { evHeavy: true, count: 8, bucketFaq: { q: "Can Scott help me decide between gas and EV in Hilliard?", a: "Yes. With public charging near Hilliard Station Park and decent EV coverage in the area, the decision usually comes down to daily miles and home charging. I'll walk you through both honestly." } }),
  },
  "dublin-oh": {
    city: "Dublin",
    bucket: "suburb-family",
    image: "river",
    h1: "Chevy SUV, EV, and Vehicle Help Near Dublin, OH",
    heroSubhead: "Shopping from Dublin? Text Scott for SUV and EV comparisons, lease-vs-finance guidance, trade-in help, and clear payment direction — no pressure, no runaround.",
    metaDesc: "Shopping new Chevys near Dublin, OH? Text Scott for family SUVs, EVs, trade help, and lease-vs-finance guidance — no pressure, real answers.",
    paragraphs: [
      "Dublin shoppers usually walk in well-informed and value a fast, honest conversation more than a long sales pitch. Scott helps you compare new Chevy SUVs, EVs, and family vehicles, and figure out lease-vs-finance based on how you actually drive and how long you tend to keep a vehicle.",
      "From Historic Dublin and Bridge Park to Muirfield and the Sawmill Road corridor, the typical Dublin shopper compares three or four vehicles before committing. I'll help you cut that down to the right two, talk through the lease vs finance math honestly, and walk you through whether an EV actually fits your driving pattern before you commit to one.",
      "Text Scott a quick rundown of what you drive now and what you're after — SUV, EV, family vehicle, or upgrade. You'll get a real answer back instead of a generic pitch.",
    ],
    buyerIntent: ["familySUVs","chevyEVs","leaseVsFinance","tradeUpgrades"],
    models: ["Equinox","Traverse","Tahoe","Suburban","Blazer EV","Equinox EV","Silverado EV"],
    evEmphasis: "high",
    nearby: ["hilliard-oh","columbus-oh","westerville-oh","plain-city-oh","marysville-oh","delaware-oh"],
    faqs: FAQ_BUILD("Dublin", { evHeavy: true, count: 8, bucketFaq: { q: "Do you help compare lease vs finance for Dublin shoppers?", a: "Yes. Lease usually fits Dublin drivers who like a newer vehicle every 2–3 years and stay within mileage. Finance usually fits drivers who plan to keep it longer or drive more. I'll walk through both with real numbers." } }),
  },
  "gahanna-oh": {
    city: "Gahanna",
    bucket: "suburb-family",
    image: "suburb",
    h1: "Chevy SUV and EV Help Near Gahanna, OH",
    heroSubhead: "Shopping from Gahanna? Text Scott for SUV and EV help, commuter vehicle options, trade-in guidance, and clear payment direction.",
    metaDesc: "Need a Chevy near Gahanna, OH? Text Scott for SUV and EV help, commuter vehicle options, trade-in guidance, and payment direction.",
    paragraphs: [
      "Gahanna is heavy on commuters and families balancing school, work near the airport corridor, and weekend life. Scott helps you compare new and used SUVs, look at EVs honestly, and figure out what payment range actually makes sense for the household.",
      "Whether you live near Creekside, Olde Gahanna, or commute up 270 to the Polaris corridor, the right vehicle here usually has the right mix of cargo space, fuel cost, and reliability. I help Gahanna drivers sort it by what you actually drive — not by what's most expensive on the lot.",
      "Text a few details about your commute and the payment range you want to land on. You'll get a realistic direction back, not a sales pitch.",
    ],
    buyerIntent: ["familySUVs","chevyEVs","commuterSUVs","tradeHelp"],
    models: ["Equinox","Trax","Traverse","Blazer EV","Equinox EV","Used SUVs"],
    evEmphasis: "high",
    nearby: ["columbus-oh","westerville-oh","reynoldsburg-oh","pickerington-oh","whitehall-oh","pataskala-oh"],
    faqs: FAQ_BUILD("Gahanna", { evHeavy: true, count: 8, bucketFaq: { q: "Can Scott help me figure out if a third row is worth it from Gahanna?", a: "Yes. If you mostly carpool or run kids around, a Traverse or Suburban third row makes life easier. If it's just family of four, an Equinox or Blazer is often the better fit. Send the basics and Scott will steer you straight." } }),
  },
  "westerville-oh": {
    city: "Westerville",
    bucket: "suburb-family",
    image: "downtown",
    h1: "Chevy SUV, EV, and Payment Help Near Westerville, OH",
    heroSubhead: "Shopping from Westerville? Text Scott for family SUV help, EV comparisons, lease/finance guidance, and clear payment direction.",
    metaDesc: "Buying a Chevy near Westerville, OH? Text Scott for family SUVs, commuter SUVs, EV options, and clear payment guidance — no pressure.",
    paragraphs: [
      "Westerville drivers usually want a family-friendly SUV, a reasonable commute setup, and a payment that won't surprise them. Scott helps you compare new and used Chevy options and weigh EV vs gas honestly — Westerville's PowerUp charging coverage makes the EV conversation more real here than in a lot of Ohio towns.",
      "From Uptown Westerville to the Polaris corridor, the typical shopper here is comparing three-row SUVs, EVs, and maybe a truck for the second vehicle. I'll help you talk through the lease vs finance math, trade equity, and whether an EV truly fits your driving — not just whether one is available.",
      "Send a quick text with what you're driving now and what you want to land on each month. You'll get a real direction back.",
    ],
    buyerIntent: ["familySUVs","chevyEVs","leaseVsFinance","commuterSUVs"],
    models: ["Equinox","Traverse","Tahoe","Trax","Blazer EV","Equinox EV"],
    evEmphasis: "high",
    nearby: ["columbus-oh","gahanna-oh","dublin-oh","delaware-oh","pickerington-oh","newark-oh"],
    faqs: FAQ_BUILD("Westerville", { evHeavy: true, count: 8, bucketFaq: { q: "Does Westerville's PowerUp charging help if I'm thinking EV?", a: "It helps. Local public charging coverage makes EV ownership less stressful, but for most Westerville drivers home charging still matters more than public — I'll walk you through both." } }),
  },
  "reynoldsburg-oh": {
    city: "Reynoldsburg",
    bucket: "city-budget",
    image: "suburb",
    h1: "Used Vehicle and Payment Help Near Reynoldsburg, OH",
    heroSubhead: "Shopping from Reynoldsburg? Text Scott for used SUVs, commuter vehicles, payment-focused buying help, and trade-in support.",
    metaDesc: "Shopping a Chevy near Reynoldsburg, OH? Text Scott for used SUVs, commuter vehicles, payment help, and trade-in support — no pressure.",
    paragraphs: [
      "Reynoldsburg drivers usually run longer commutes than the metro average, which means the right vehicle has to balance fuel cost, reliability, and a payment you can live with. Scott helps you sort through used SUVs, commuter cars, and used trucks honestly — without pushing you into more vehicle than you actually need.",
      "Whether you're heading west into Columbus, east toward Pataskala, or south to Pickerington, the typical Reynoldsburg shopper cares more about total monthly cost than features. I'll walk through trade-in value, payoff, payment range, and used-vs-new so you don't end up over-vehicle for what you actually drive.",
      "Text Scott a quick rundown of your commute and target monthly. You'll get a realistic direction back, not a pitch.",
    ],
    buyerIntent: ["affordableHelp","paymentFocused","tradeHelp","commuterSUVs"],
    models: ["Trax","Equinox","Malibu","Used cars","Used SUVs","Used under $25k"],
    evEmphasis: "low",
    nearby: ["columbus-oh","gahanna-oh","pickerington-oh","whitehall-oh","pataskala-oh","newark-oh"],
    faqs: FAQ_BUILD("Reynoldsburg", { evHeavy: false, count: 8, bucketFaq: { q: "Does Scott help shoppers with a longer commute from Reynoldsburg?", a: "Yes. Fuel cost over a long commute can quietly add hundreds a month. I'll help you compare gas economy, total monthly cost, and reliability for the kind of miles you actually drive." } }),
  },
  "pickerington-oh": {
    city: "Pickerington",
    bucket: "suburb-family",
    image: "downtown",
    h1: "Chevy SUV, Truck, and Trade-In Help Near Pickerington, OH",
    heroSubhead: "Shopping from Pickerington? Text Scott for family SUVs, trucks, newer Chevy vehicles, and trade-in upgrade help.",
    metaDesc: "Need a Chevy near Pickerington, OH? Text Scott for family SUVs, trucks, newer vehicles, and trade-in upgrade help — no pressure.",
    paragraphs: [
      "Pickerington shoppers often have a vehicle to trade and they're moving up — to a bigger SUV, a newer truck, or finally an EV. Scott helps you figure out trade equity, what payment range fits the upgrade, and which Chevy actually makes sense for the next 4–6 years.",
      "Whether you live near Olde Pickerington Village, near the schools, or out toward Toll Gate, the typical shopper here wants more space, more towing, or more tech than what they're driving now. I'll help you weigh new vs used, talk through what your trade is realistically worth, and steer the conversation away from sticker-shock surprises later.",
      "Send a text with your current vehicle and what you want next. I'll come back with what your trade likely affects and what payment range fits.",
    ],
    buyerIntent: ["familySUVs","chevyTrucks","tradeUpgrades","newerVehicles"],
    models: ["Equinox","Traverse","Tahoe","Silverado","Colorado","Equinox EV","Blazer EV"],
    evEmphasis: "high",
    nearby: ["columbus-oh","reynoldsburg-oh","canal-winchester-oh","pataskala-oh","lancaster-oh","newark-oh"],
    faqs: FAQ_BUILD("Pickerington", { evHeavy: true, count: 8, bucketFaq: { q: "Can Scott help upgrade my current vehicle from Pickerington?", a: "Yes. Upgrade conversations are mostly about trade equity, payoff, and target monthly. Send the basics on what you drive now and I'll come back with what the next step actually looks like." } }),
  },
  "groveport-oh": {
    city: "Groveport",
    bucket: "city-budget",
    image: "country",
    h1: "New and Used Chevy Help Near Groveport, OH",
    heroSubhead: "Shopping from Groveport? Text Scott for affordable new Chevys, used SUVs, used trucks, and trade-in support — no pressure.",
    metaDesc: "Shopping a Chevy near Groveport, OH? Text Scott for affordable new and used Chevy options, used SUVs, used trucks, and trade-in support.",
    paragraphs: [
      "Groveport is a practical-vehicle market — work trucks, used SUVs, and family vehicles that have to last. Scott helps you sort through new and used options without inflating the budget or pushing you into trim levels you'll never use.",
      "Whether you're working out of the Rickenbacker corridor or driving family up to Columbus daily, the right vehicle here usually has to do more than one job. I'll help you weigh used vs new, talk through the payoff and trade math, and find something that earns its payment.",
      "Text Scott a quick note about how you'll use the vehicle and what payment you want to stay near. I'll come back with real options.",
    ],
    buyerIntent: ["affordableHelp","usedTrucksSUVs","workTrucks","tradeHelp"],
    models: ["Trax","Equinox","Silverado","Colorado","Used SUVs","Used trucks","Used under $25k"],
    evEmphasis: "low",
    nearby: ["grove-city-oh","columbus-oh","canal-winchester-oh","pickerington-oh","reynoldsburg-oh","circleville-oh"],
    faqs: FAQ_BUILD("Groveport", { evHeavy: false, count: 7 }),
  },
  "canal-winchester-oh": {
    city: "Canal Winchester",
    bucket: "suburb-family",
    image: "downtown",
    h1: "Chevy SUV, Truck, and Payment Help Near Canal Winchester, OH",
    heroSubhead: "Shopping from Canal Winchester? Text Scott for SUVs, trucks, family vehicles, and clear payment guidance.",
    metaDesc: "Buying a Chevy near Canal Winchester, OH? Text Scott for SUVs, trucks, family vehicles, and clear payment guidance.",
    paragraphs: [
      "Canal Winchester sits between city commuting and more rural driving, which is why most shoppers here want a real SUV or a truck — not a compact crossover. Scott helps you compare options honestly without pushing into more vehicle than you actually need.",
      "Whether you're heading downtown for work or running south toward Lancaster on weekends, the typical Canal Winchester driver wants comfortable highway miles, towing if needed, and a payment that won't surprise you later. I'll walk you through trade-in details and what affects your payment before you fall in love with the wrong vehicle.",
      "Send Scott a text with what you're driving now and the type of vehicle you want next. You'll get a real direction back.",
    ],
    buyerIntent: ["chevyTrucks","familySUVs","tradeHelp","paymentFocused"],
    models: ["Silverado","Equinox","Traverse","Tahoe","Trax","Used SUVs"],
    evEmphasis: "low",
    nearby: ["groveport-oh","grove-city-oh","columbus-oh","pickerington-oh","circleville-oh","lancaster-oh"],
    faqs: FAQ_BUILD("Canal Winchester", { evHeavy: false, count: 8, bucketFaq: { q: "Should Canal Winchester drivers consider AWD?", a: "Most years it's worth it. The mix of suburb and rural driving here means winter weather and country roads come up. Scott will help you weigh AWD-on-SUV versus 4WD-on-truck for your actual driving." } }),
  },
  "ashville-oh": {
    city: "Ashville",
    bucket: "truck-distance",
    image: "highway",
    h1: "Chevy Truck and SUV Help Near Ashville, OH",
    heroSubhead: "Shopping from Ashville? Text Scott for trucks, AWD SUVs, used trucks, and trade-in help — worth doing most of it by text first.",
    metaDesc: "Need a Chevy near Ashville, OH? Text Scott for trucks, SUVs, AWD options, and trade-in help — worth doing most of it before driving up.",
    paragraphs: [
      "Ashville drivers usually want a real truck or a capable SUV, and they want a fast text-first conversation, not a long sales process. Scott helps you sort through new and used Chevy trucks and SUVs and value your trade without you needing to make the drive twice.",
      "If you're driving up 23 or in from the surrounding country roads, the right vehicle here has to handle real weather, real towing, and real winters. I'll help you compare Silverado, Colorado, Tahoe, and the right used trucks against your trade and your payment range — and you can do most of it by text first.",
      "Text Scott your current vehicle and what you want next. You'll save the wasted trip.",
    ],
    buyerIntent: ["chevyTrucks","awdNeeds","longerDriveShopping","tradeHelp"],
    models: ["Silverado","Colorado","Equinox","Traverse","Tahoe","Used trucks","AWD SUVs"],
    evEmphasis: "low",
    nearby: ["circleville-oh","grove-city-oh","groveport-oh","london-oh","lancaster-oh","chillicothe-oh"],
    faqs: FAQ_BUILD("Ashville", { evHeavy: false, count: 7, usedAnchor: "Used trucks, used SUVs, fresh trades — Ashville drivers especially shop trucks and SUVs.", bucketFaq: { q: "Is it worth the drive from Ashville?", a: "Most of the deal can happen by text — vehicle selection, trade ballpark, payment range, paperwork plan. By the time you drive up, the trip is short." } }),
  },
  "circleville-oh": {
    city: "Circleville",
    bucket: "truck-distance",
    image: "highway",
    h1: "Chevy Truck, SUV, and Trade-In Help Near Circleville, OH",
    heroSubhead: "Shopping from Circleville? Text Scott for trucks, used trucks, SUVs, trade help, and a no-pressure plan before the drive.",
    metaDesc: "Shopping a Chevy near Circleville, OH? Text Scott for trucks, used trucks, SUVs, longer-drive reliability, and trade help.",
    paragraphs: [
      "Circleville is truck country. Most shoppers I help here are after a Silverado, a Colorado, a Tahoe, or a strong used truck — and they care about the drive holding up over the long haul. Scott helps you compare, value your trade, and skip the multiple-trip dealership experience.",
      "Whether you're north on 23 toward Columbus or running between Circleville, Ashville, and Chillicothe, the right vehicle here has to handle highway miles, weather, and work. I'll walk you through trucks and SUVs honestly — including what's worth the new sticker and what makes more sense as a used trade.",
      "Send a text with the truck or SUV you're considering and what you're driving now. I'll come back with real direction, not a pitch.",
    ],
    buyerIntent: ["chevyTrucks","usedTrucksSUVs","longerDriveShopping","tradeHelp"],
    models: ["Silverado","Colorado","Tahoe","Suburban","Traverse","Used trucks","Used SUVs"],
    evEmphasis: "low",
    nearby: ["ashville-oh","chillicothe-oh","grove-city-oh","lancaster-oh","groveport-oh","london-oh"],
    faqs: FAQ_BUILD("Circleville", { evHeavy: false, count: 7, usedAnchor: "Used trucks and used SUVs are what most Circleville shoppers ask about first.", bucketFaq: { q: "Does Scott help with longer-drive shopping from Circleville?", a: "Yes. Most of the work happens by text — vehicle selection, trade, payment plan. You drive up once with a plan, not three times to bounce between options." } }),
  },
  "london-oh": {
    city: "London",
    bucket: "truck-distance",
    image: "country",
    h1: "Chevy Truck and Used Vehicle Help Near London, OH",
    heroSubhead: "Shopping from London? Text Scott for trucks, SUVs, used vehicles, and trade-in help — no need to make the drive twice.",
    metaDesc: "Buying a Chevy near London, OH? Text Scott for trucks, SUVs, used vehicles, and trade-in help — handle most of it before driving up.",
    paragraphs: [
      "London drivers usually shop on their own time and don't want to make the drive into the metro twice. Scott works text-first so you can compare trucks, SUVs, and used options, and value your trade, before you ever come up.",
      "Whether you're east toward Grove City or south toward I-70, the right vehicle here usually has to balance highway commuting with weekend use. I'll help you compare Silverado, Colorado, Equinox, and Tahoe options against the right used picks — including what your trade is realistically worth before you start shopping.",
      "Text Scott your current vehicle, what you want next, and your payment range. You'll save a wasted trip.",
    ],
    buyerIntent: ["chevyTrucks","usedTrucksSUVs","longerDriveShopping","tradeHelp"],
    models: ["Silverado","Colorado","Equinox","Traverse","Tahoe","Used trucks","Used SUVs"],
    evEmphasis: "low",
    nearby: ["grove-city-oh","circleville-oh","plain-city-oh","ashville-oh","marysville-oh","columbus-oh"],
    faqs: FAQ_BUILD("London", { evHeavy: false, count: 8, bucketFaq: { q: "How much of a London Chevy deal can happen by text?", a: "Most of it. Vehicle selection, trade ballpark, payment direction, even the paperwork plan can be lined up before you drive in. By the time you arrive the trip is short." } }),
  },
  "plain-city-oh": {
    city: "Plain City",
    bucket: "suburb-family",
    image: "country",
    h1: "Chevy Truck, SUV, and EV Help Near Plain City, OH",
    heroSubhead: "Shopping from Plain City? Text Scott for trucks, family SUVs, commuter vehicles, and EV comparisons — no pressure.",
    metaDesc: "Need a Chevy near Plain City, OH? Text Scott for trucks, family SUVs, commuter vehicles, and EV-curious comparisons.",
    paragraphs: [
      "Plain City sits between farmland and suburb life, which makes the vehicle mix here interesting — trucks, family SUVs, the occasional EV. Scott helps you compare honestly without trying to push someone who needs a Silverado into a sedan, or someone who really wants an EV into a gas SUV they don't need.",
      "Whether you're heading east into Dublin or working closer to home, the right vehicle here usually has space, capability, and real-world fuel sense. I'll walk you through new Chevy trucks and SUVs, EV options if you're curious, and what your trade is realistically worth.",
      "Text Scott a quick note about how you actually drive — daily miles, weekends, towing — and I'll come back with the right vehicle direction.",
    ],
    buyerIntent: ["chevyTrucks","familySUVs","chevyEVs","tradeUpgrades"],
    models: ["Silverado","Equinox","Traverse","Tahoe","Equinox EV","Blazer EV","Used trucks"],
    evEmphasis: "high",
    nearby: ["dublin-oh","marysville-oh","hilliard-oh","columbus-oh","london-oh","delaware-oh"],
    faqs: FAQ_BUILD("Plain City", { evHeavy: true, count: 8, bucketFaq: { q: "Does Scott help if I'm split between a truck and an SUV from Plain City?", a: "Yes — that's actually one of the most common Plain City conversations. The honest tiebreaker is usually whether you tow, haul, or drive country roads in weather. I'll help you decide before you commit." } }),
  },
  "delaware-oh": {
    city: "Delaware",
    bucket: "suburb-family",
    image: "downtown",
    h1: "Chevy SUV, Truck, and EV Help Near Delaware, OH",
    heroSubhead: "Shopping from Delaware? Text Scott for SUVs, trucks, EV comparisons, and clear trade and payment help.",
    metaDesc: "Shopping a Chevy near Delaware, OH? Text Scott for SUVs, trucks, EV-curious comparisons, and clear trade and payment help.",
    paragraphs: [
      "Delaware shoppers usually fall into one of three buckets — family SUV upgrade, truck buyer, or EV curious. Scott helps you compare honestly across all three and figure out what your trade is realistically worth before you start.",
      "Whether you're near downtown Delaware, around OWU, or working south toward Polaris, the right vehicle here usually has to handle a real commute and family duty. I'll walk you through Tahoe-vs-Traverse, Silverado-vs-Colorado, EV-vs-gas, and lease-vs-finance with straight answers — not a sales script.",
      "Text Scott your current vehicle and the kind of vehicle you want next. You'll get a real direction back.",
    ],
    buyerIntent: ["familySUVs","chevyTrucks","chevyEVs","leaseVsFinance"],
    models: ["Equinox","Traverse","Tahoe","Silverado","Colorado","Equinox EV","Blazer EV"],
    evEmphasis: "high",
    nearby: ["westerville-oh","marysville-oh","dublin-oh","columbus-oh","plain-city-oh","newark-oh"],
    faqs: FAQ_BUILD("Delaware", { evHeavy: true, count: 8, bucketFaq: { q: "Can Scott help me compare a truck and an SUV from Delaware?", a: "Yes. Trucks win on towing, hauling, and bed utility. SUVs win on weather sealing, comfort, and family use. I'll walk through both and how each affects your payment differently." } }),
  },
  "marysville-oh": {
    city: "Marysville",
    bucket: "suburb-family",
    image: "downtown",
    h1: "Chevy SUV, Truck, and Payment Help Near Marysville, OH",
    heroSubhead: "Shopping from Marysville? Text Scott for commuter vehicles, family SUVs, trucks, and payment help.",
    metaDesc: "Buying a Chevy near Marysville, OH? Text Scott for commuter vehicles, family SUVs, trucks, and payment help.",
    paragraphs: [
      "Marysville drivers are commuters first — usually east toward Dublin or Columbus, or to one of the larger employers right in town. Scott helps you find a vehicle that handles the daily drive without eating up your budget.",
      "Whether you live near Uptown, work at the local plants, or are running family back and forth, the right vehicle here usually has to last and stay reasonable on gas. I'll help you compare new and used Chevy options, value your trade, and talk through what payment actually fits.",
      "Text Scott a quick note about your commute, current vehicle, and target payment. You'll get a real direction back.",
    ],
    buyerIntent: ["commuterSUVs","familySUVs","chevyTrucks","paymentFocused"],
    models: ["Equinox","Traverse","Trax","Silverado","Colorado","Used SUVs"],
    evEmphasis: "med",
    nearby: ["dublin-oh","plain-city-oh","delaware-oh","hilliard-oh","columbus-oh","london-oh"],
    faqs: FAQ_BUILD("Marysville", { evHeavy: false, count: 8, bucketFaq: { q: "What's a smart commuter vehicle for Marysville drivers?", a: "Depends on the commute length and trade situation. Equinox and Trax are usually the gas-fuel winners for daily Marysville miles; Traverse or a used SUV makes sense if family use matters too. Send your commute and Scott will narrow it." } }),
  },
  "pataskala-oh": {
    city: "Pataskala",
    bucket: "truck-distance",
    image: "country",
    h1: "Chevy Truck, SUV, and Trade-In Help Near Pataskala, OH",
    heroSubhead: "Shopping from Pataskala? Text Scott for trucks, commuter SUVs, and trade-in help — handle most of it by text.",
    metaDesc: "Shopping a Chevy near Pataskala, OH? Text Scott for trucks, SUVs, commuter vehicles, and trade-in help.",
    paragraphs: [
      "Pataskala drivers usually run longer commutes and shop with a mix of practical SUVs, commuter crossovers, and trucks. Scott helps you compare new and used Chevy options, weigh trade equity, and figure out a payment that works for the drive.",
      "Whether you're commuting west into Columbus or east toward Newark and Heath, the right vehicle here usually balances fuel cost and capability. I'll walk you through Silverado-vs-Colorado, Equinox-vs-Traverse, and trade-in details before you make the drive in.",
      "Text Scott your commute and target monthly. You'll get a realistic direction back.",
    ],
    buyerIntent: ["chevyTrucks","commuterSUVs","tradeHelp","longerDriveShopping"],
    models: ["Silverado","Colorado","Equinox","Traverse","Trax","Used trucks"],
    evEmphasis: "low",
    nearby: ["reynoldsburg-oh","pickerington-oh","newark-oh","columbus-oh","gahanna-oh","westerville-oh"],
    faqs: FAQ_BUILD("Pataskala", { evHeavy: false, count: 8, bucketFaq: { q: "Can Scott help me weigh a commuter SUV against a truck from Pataskala?", a: "Yes. Commuter SUVs (Equinox, Traverse) usually win on fuel cost. Trucks (Silverado, Colorado) win when you actually tow or haul. Send your miles and use case and Scott will tell you the honest answer for your budget." } }),
  },
  "lancaster-oh": {
    city: "Lancaster",
    bucket: "truck-distance",
    image: "downtown",
    h1: "Chevy Truck, SUV, and Payment Help Near Lancaster, OH",
    heroSubhead: "Shopping from Lancaster? Text Scott for trucks, used SUVs, longer-drive shopping help, and clear payment direction.",
    metaDesc: "Need a Chevy near Lancaster, OH? Text Scott for trucks, used SUVs, payment help, and longer-drive shopping support.",
    paragraphs: [
      "Lancaster shoppers often want a real truck or a capable SUV, and they want to handle most of the conversation by text before driving up. Scott helps you sort through trucks, used SUVs, and payment guidance without wasting your trip.",
      "Whether you're on 33 heading toward Columbus or south through Hocking County, the right vehicle here usually has to handle highway miles, the occasional load, and Ohio winters. I'll walk you through Silverado, Colorado, Tahoe, used trucks, and what your trade is realistically worth.",
      "Text Scott what you're driving now and the kind of vehicle you want next. You'll save the wasted drive.",
    ],
    buyerIntent: ["chevyTrucks","usedTrucksSUVs","longerDriveShopping","paymentFocused"],
    models: ["Silverado","Colorado","Tahoe","Traverse","Equinox","Used trucks","Used SUVs"],
    evEmphasis: "low",
    nearby: ["pickerington-oh","canal-winchester-oh","circleville-oh","newark-oh","chillicothe-oh","grove-city-oh"],
    faqs: FAQ_BUILD("Lancaster", { evHeavy: false, count: 8, bucketFaq: { q: "Is a used truck better than new for Lancaster shoppers?", a: "Often, yes — especially if you tow or haul and don't need the newest tech. A clean used Silverado or Colorado at lower payment territory beats a new one stretched over too long a term. Scott will help you weigh both honestly." } }),
  },
  "chillicothe-oh": {
    city: "Chillicothe",
    bucket: "truck-distance",
    image: "highway",
    h1: "Chevy Truck and Trade-In Help Near Chillicothe, OH",
    heroSubhead: "Shopping from Chillicothe? Text Scott for trucks, used trucks, SUVs, and trade help — handle most of it before the drive.",
    metaDesc: "Buying a Chevy near Chillicothe, OH? Text Scott for trucks, longer-distance shopping, trade help, and practical SUVs.",
    paragraphs: [
      "Chillicothe drivers usually want a truck or a big-enough SUV that handles real driving — and they don't want to make the trip into the Columbus area unless it's worth it. Scott works text-first so you can shop, value your trade, and compare options before you ever drive up.",
      "Whether you're commuting up 23 or staying local, the right vehicle here has to earn its payment. I'll help you compare Silverado, Colorado, Tahoe, Suburban, and used truck options honestly — and tell you when used makes more sense than new.",
      "Text Scott your current vehicle and what you want next. You'll save the trip.",
    ],
    buyerIntent: ["chevyTrucks","usedTrucksSUVs","longerDriveShopping","tradeHelp"],
    models: ["Silverado","Colorado","Tahoe","Suburban","Traverse","Used trucks","Used SUVs"],
    evEmphasis: "low",
    nearby: ["circleville-oh","ashville-oh","lancaster-oh","grove-city-oh","london-oh","newark-oh"],
    faqs: FAQ_BUILD("Chillicothe", { evHeavy: false, count: 8, usedAnchor: "Used trucks, used SUVs, and one-owner trades — the bread and butter for Chillicothe shoppers.", bucketFaq: { q: "Is the drive from Chillicothe worth it?", a: "Most Chillicothe shoppers do 80% of the deal by text. By the time you drive up, the vehicle is picked, the trade is ballparked, and the payment direction is clear. One trip, not three." } }),
  },
  "newark-oh": {
    city: "Newark",
    bucket: "city-budget",
    image: "downtown",
    h1: "Used Vehicle, Chevy Truck, and Payment Help Near Newark, OH",
    heroSubhead: "Shopping from Newark? Text Scott for used vehicles, trucks, affordable SUVs, payment help, and trade support.",
    metaDesc: "Shopping a Chevy near Newark, OH? Text Scott for used vehicles, trucks, affordable SUVs, payment help, and trade-in support.",
    paragraphs: [
      "Newark shoppers usually start with a payment range and work backwards. Scott helps you sort through used vehicles, affordable new options, and trucks against a real budget — not against what the lot wants to sell you.",
      "Whether you're near downtown Newark, working through Heath, or running the 16 corridor, the right vehicle here usually balances total monthly cost and reliability. I'll walk you through used vs new, talk through trade equity, and help you find a payment that actually fits.",
      "Text Scott your target monthly and what you're driving now. You'll get a real direction back, not a pitch.",
    ],
    buyerIntent: ["affordableHelp","paymentFocused","usedTrucksSUVs","tradeHelp"],
    models: ["Trax","Equinox","Silverado","Colorado","Used cars","Used SUVs","Used trucks","Used under $25k"],
    evEmphasis: "low",
    nearby: ["pataskala-oh","reynoldsburg-oh","pickerington-oh","westerville-oh","lancaster-oh","columbus-oh"],
    faqs: FAQ_BUILD("Newark", { evHeavy: false, count: 8, bucketFaq: { q: "Can Scott help me find a used Chevy under a certain price near Newark?", a: "Yes. Tell me your max price or target monthly and I'll help filter used inventory to vehicles that actually fit — not vehicles that just look close." } }),
  },
  "whitehall-oh": {
    city: "Whitehall",
    bucket: "city-budget",
    image: "suburb",
    h1: "Affordable New and Used Vehicle Help Near Whitehall, OH",
    heroSubhead: "Shopping from Whitehall? Text Scott for affordable new and used Chevy options, payment help, and trade-in support.",
    metaDesc: "Need a Chevy near Whitehall, OH? Text Scott for affordable new and used options, compact SUVs, payment help, and trade-in support.",
    paragraphs: [
      "Whitehall shoppers are often working with a clear payment ceiling and want a vehicle that earns its keep. Scott helps you sort through affordable new Chevy options, used SUVs, used cars, and trade-in math without inflating the budget.",
      "Whether you're near Main Street, working east toward Reynoldsburg, or commuting downtown, the right vehicle here usually means lower payment territory and reliable miles. I'll walk you through Trax, Equinox, Malibu, used SUVs, and budget-friendly used trucks honestly.",
      "Text Scott your target monthly and what you're driving now. You'll get a real direction back.",
    ],
    buyerIntent: ["affordableHelp","paymentFocused","firstTimeBuyer","usedTrucksSUVs"],
    models: ["Trax","Equinox","Malibu","Used cars","Used SUVs","Used under $25k"],
    evEmphasis: "low",
    nearby: ["columbus-oh","reynoldsburg-oh","gahanna-oh","grove-city-oh","groveport-oh","pataskala-oh"],
    faqs: FAQ_BUILD("Whitehall", { evHeavy: false, count: 8, bucketFaq: { q: "Can Scott help Whitehall shoppers with limited credit?", a: "Yes — Scott helps you understand what affects approval (score, income, debt, down, trade equity, vehicle choice) so the lender step doesn't catch you off guard. No promises, just an honest direction." } }),
  },
};

// Re-export archetype map so the page component can render it.
export const BUYER_INTENT_ARCHETYPES = ARCHETYPES;
